import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import { getBaseCurrencyCode, getCurrencyCode } from 'utils/helpers/currency/currencyHelpers';

type CurrencyCode = string;

interface BuildTemplateValueKeysParams {
    productsAmount: number;
    additionalsAmount: number;
    specificationAmount: number;
    servicesAmount?: number;
    otherItemsAmount?: number;
    productsAmountVat: number;
    additionalsAmountVat: number;
    specificationAmountVat: number;
    servicesAmountVat?: number;
    otherItemsAmountVat?: number;
    currencyCode: CurrencyCode;
}

const KEY_REGEX = /\{([^{}\n]+)\}/g;

const stripKeyNoise = (raw: string): string =>
    String(raw || '')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&#8203;/g, '')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .trim();

const lookupTemplateValue = (keyName: string, valuesMap: Record<string, string>): string | undefined => {
    const key = stripKeyNoise(keyName);
    if (!key) return undefined;
    if (Object.prototype.hasOwnProperty.call(valuesMap, key)) return valuesMap[key];

    const underscored = key.replace(/\./g, '_');
    if (underscored !== key && Object.prototype.hasOwnProperty.call(valuesMap, underscored)) {
        return valuesMap[underscored];
    }
    const dotted = key.replace(/_/g, '.');
    if (dotted !== key && Object.prototype.hasOwnProperty.call(valuesMap, dotted)) {
        return valuesMap[dotted];
    }

    const lower = key.toLowerCase();
    const lowerMatch = Object.keys(valuesMap).find((mapKey) => mapKey.toLowerCase() === lower);
    return lowerMatch != null ? valuesMap[lowerMatch] : undefined;
};

const getStringArray = (t: TFunction, key: string): string[] => {
    const value = t(key, { returnObjects: true });
    return Array.isArray(value) ? (value as string[]) : [];
};

const getUkrainianPlural = (n: number, forms: [string, string, string]): string => {
    const nAbs = Math.abs(n) % 100;
    const n1 = nAbs % 10;
    if (nAbs > 10 && nAbs < 20) return forms[2];
    if (n1 > 1 && n1 < 5) return forms[1];
    if (n1 === 1) return forms[0];
    return forms[2];
};

const createNumberToWordsEn = (t: TFunction) => {
    const ones = getStringArray(t, 'numbers.ones');
    const teens = getStringArray(t, 'numbers.teens');
    const tens = getStringArray(t, 'numbers.tens');

    const numberToWordsEn = (value: number): string => {
        const n = Math.floor(Math.max(0, Number(value) || 0));
        if (n < 10) return ones[n];
        if (n < 20) return teens[n - 10];
        if (n < 100) return `${tens[Math.floor(n / 10)]}${n % 10 ? ` ${ones[n % 10]}` : ''}`;
        if (n < 1000) {
            return `${ones[Math.floor(n / 100)]} ${t('scale.hundred')}${n % 100 ? ` ${numberToWordsEn(n % 100)}` : ''}`;
        }
        if (n < 1_000_000) {
            return `${numberToWordsEn(Math.floor(n / 1000))} ${t('scale.thousand')}${n % 1000 ? ` ${numberToWordsEn(n % 1000)}` : ''}`;
        }
        if (n < 1_000_000_000) {
            return `${numberToWordsEn(Math.floor(n / 1_000_000))} ${t('scale.million')}${n % 1_000_000 ? ` ${numberToWordsEn(n % 1_000_000)}` : ''}`;
        }
        return `${numberToWordsEn(Math.floor(n / 1_000_000_000))} ${t('scale.billion')}${n % 1_000_000_000 ? ` ${numberToWordsEn(n % 1_000_000_000)}` : ''}`;
    };

    return numberToWordsEn;
};

const createNumberToWordsUa = (t: TFunction) => {
    const onesM = getStringArray(t, 'numbers.onesM');
    const onesF = getStringArray(t, 'numbers.onesF');
    const teens = getStringArray(t, 'numbers.teens');
    const tens = getStringArray(t, 'numbers.tens');
    const hundreds = getStringArray(t, 'numbers.hundreds');

    const tripletToWordsUa = (n: number, feminine = false): string => {
        if (!n) return '';
        const words: string[] = [];
        const hundredsPart = Math.floor(n / 100);
        const tensUnits = n % 100;
        const tensPart = Math.floor(tensUnits / 10);
        const units = tensUnits % 10;

        if (hundredsPart) words.push(hundreds[hundredsPart]);
        if (tensUnits >= 10 && tensUnits < 20) {
            words.push(teens[tensUnits - 10]);
        } else {
            if (tensPart) words.push(tens[tensPart]);
            if (units) words.push((feminine ? onesF : onesM)[units]);
        }

        return words.join(' ');
    };

    const numberToWordsUa = (value: number): string => {
        let n = Math.floor(Math.max(0, Number(value) || 0));
        if (n === 0) return onesM[0];

        const groups: Array<{ forms: [string, string, string]; feminine: boolean }> = [
            { forms: ['', '', ''], feminine: false },
            { forms: getStringArray(t, 'groups.thousand') as [string, string, string], feminine: true },
            { forms: getStringArray(t, 'groups.million') as [string, string, string], feminine: false },
            { forms: getStringArray(t, 'groups.billion') as [string, string, string], feminine: false },
        ];

        const parts: string[] = [];
        let groupIndex = 0;
        while (n > 0 && groupIndex < groups.length) {
            const triplet = n % 1000;
            if (triplet) {
                const group = groups[groupIndex];
                const words = tripletToWordsUa(triplet, group.feminine);
                const form = group.forms[0] ? getUkrainianPlural(triplet, group.forms) : '';
                parts.unshift([words, form].filter(Boolean).join(' '));
            }
            n = Math.floor(n / 1000);
            groupIndex += 1;
        }

        return parts.join(' ').trim();
    };

    return numberToWordsUa;
};

/** Resolve i18n currency word-forms key, or null for numeric fallback. */
const resolveAmountWordsCurrencyKey = (currencyCode: CurrencyCode): 'usd' | 'eur' | 'uah' | null => {
    const code = String(currencyCode || '').toUpperCase();
    const base = getBaseCurrencyCode();
    const local = getCurrencyCode();

    if (code === base || (code === 'USD' && base === 'USD') || (code === 'EUR' && base === 'EUR')) {
        return base === 'EUR' ? 'eur' : 'usd';
    }

    if ((code === local || code === 'UAH') && local === 'UAH') {
        return 'uah';
    }

    return null;
};

const createAmountToText = (t: TFunction, isUk: boolean) => {
    const numberToWordsEn = createNumberToWordsEn(t);
    const numberToWordsUa = createNumberToWordsUa(t);

    return (amount: number, currencyCode: CurrencyCode): string => {
        const safeAmount = Math.max(0, Number(amount) || 0);
        const major = Math.floor(safeAmount);
        const minor = Math.round((safeAmount - major) * 100);
        const currencyKey = resolveAmountWordsCurrencyKey(currencyCode);

        if (!currencyKey) {
            const code = String(currencyCode || getCurrencyCode() || '').toUpperCase();
            return `${formatPrice(safeAmount)} ${code}`.trim();
        }

        if (isUk) {
            const majorWords = numberToWordsUa(major);
            const majorForms = getStringArray(t, `currency.${currencyKey}.major`) as [string, string, string];
            const minorForms = getStringArray(t, `currency.${currencyKey}.minor`) as [string, string, string];
            const majorUnit = getUkrainianPlural(major, majorForms);
            const minorUnit = getUkrainianPlural(minor, minorForms);
            return `${majorWords} ${majorUnit} ${String(minor).padStart(2, '0')} ${minorUnit}`.trim();
        }

        const majorWords = numberToWordsEn(major);
        const majorUnit = t(`currency.${currencyKey}.major_${major === 1 ? 'one' : 'other'}`);
        const minorUnit = t(`currency.${currencyKey}.minor_${minor === 1 ? 'one' : 'other'}`);
        return `${majorWords} ${majorUnit} ${String(minor).padStart(2, '0')} ${minorUnit}`.trim();
    };
};

export const replaceTemplateValueKeys = (html: string, valuesMap: Record<string, string>): string => {
    let result = String(html || '');
    if (!result || !valuesMap || !Object.keys(valuesMap).length) return result;

    // Literal replace first (handles commas/spaces/Cyrillic; avoids regex edge cases).
    Object.keys(valuesMap)
        .sort((a, b) => b.length - a.length)
        .forEach((key) => {
            const token = `{${key}}`;
            if (!result.includes(token)) return;
            result = result.split(token).join(valuesMap[key]);
        });

    // Fallback for Quill-broken tokens (tags / zero-width chars inside `{...}`).
    return result.replace(KEY_REGEX, (full, keyName: string) => {
        const value = lookupTemplateValue(keyName, valuesMap);
        return value !== undefined ? value : full;
    });
};

export type BuildTemplateValueKeysMap = (params: BuildTemplateValueKeysParams) => Record<string, string>;

export interface TemplateValueKeysHelpers {
    buildTemplateValueKeysMap: BuildTemplateValueKeysMap;
    replaceTemplateValueKeys: typeof replaceTemplateValueKeys;
}

export const createTemplateValueKeysHelpers = (
    t: TFunction,
    language: string,
): TemplateValueKeysHelpers => {
    const isUk = language.toLowerCase().startsWith('uk') || language.toLowerCase().startsWith('ua');
    const amountToText = createAmountToText(t, isUk);

    const buildTemplateValueKeysMap: BuildTemplateValueKeysMap = ({
        productsAmount,
        additionalsAmount,
        specificationAmount,
        servicesAmount = 0,
        otherItemsAmount = 0,
        productsAmountVat,
        additionalsAmountVat,
        specificationAmountVat,
        servicesAmountVat = 0,
        otherItemsAmountVat = 0,
        currencyCode,
    }) => ({
        productsAmount: formatPrice(productsAmount),
        additionalsAmount: formatPrice(additionalsAmount),
        specificationAmount: formatPrice(specificationAmount),
        servicesAmount: formatPrice(servicesAmount),
        otherItemsAmount: formatPrice(otherItemsAmount),
        productsAmountVat: formatPrice(productsAmountVat),
        additionalsAmountVat: formatPrice(additionalsAmountVat),
        specificationAmountVat: formatPrice(specificationAmountVat),
        servicesAmountVat: formatPrice(servicesAmountVat),
        otherItemsAmountVat: formatPrice(otherItemsAmountVat),
        productsAmountText: amountToText(productsAmount, currencyCode),
        additionalsAmountText: amountToText(additionalsAmount, currencyCode),
        specificationAmountText: amountToText(specificationAmount, currencyCode),
        servicesAmountText: amountToText(servicesAmount, currencyCode),
        otherItemsAmountText: amountToText(otherItemsAmount, currencyCode),
    });

    return { buildTemplateValueKeysMap, replaceTemplateValueKeys };
};

const useTemplateValueKeys = (): TemplateValueKeysHelpers => {
    const { t, i18n } = useTranslation('TemplateValueKeys');

    return useMemo(
        () => createTemplateValueKeysHelpers(t, i18n.language),
        [t, i18n.language],
    );
};

export default useTemplateValueKeys;
